import React, { useCallback, useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import { isPDFGenerationModeEnabled } from '../../lib/utils/isPDFGenerationModeEnabled'
import { formatNumber } from '../../lib/utils/formatNumber'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
)
const disableAnimations = isPDFGenerationModeEnabled()
  ? {
      animation: false,
    }
  : {}
const options = {
  disableAnimations,
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 8,
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'center',
    },
    crosshair: {
      line: {
        color: '#121124', // crosshair line color
        width: 1, // crosshair line width
      },
    },
    line: {
      color: '#FCD',
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
    datalabels: {
      display: true,
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      ticks: {
        callback: function (val) {
          return formatNumber(val)
        },
      },
    },
  },
}

const colors = [
  'rgba(255, 181, 181, 0.5)', // Light red
  'rgba(0, 56, 255, 0.5)', // Soft blue
  'rgba(181, 255, 210, 0.5)', // Soft mint green
  'rgba(255, 223, 181, 0.5)', // Light peach
  'rgba(181, 182, 255, 0.5)', // Light lavender
  'rgba(255, 181, 226, 0.5)', // Soft pink
  'rgba(181, 239, 255, 0.5)', // Light sky blue
  'rgba(255, 255, 181, 0.5)', // Soft yellow
  'rgba(181, 255, 247, 0.5)', // Light aqua
  'rgba(214, 181, 255, 0.5)', // Light purple
  'rgba(255, 201, 181, 0.5)', // Soft coral
  'rgba(181, 255, 181, 0.5)', // Light pastel green
]

const StackedBarChartV2 = ({
  data,
  isLoading = false,
  isPreviousData = false,
  xKey,
  customOptions,
  groupKey,
  yKey,
}: {
  isLoading: boolean
  isPreviousData?: boolean
  xKey: string
  groupKey: string
  yKey: string
  data: Array<Record<string, string | number>>
  customOptions?: Record<string, unknown>
}): JSX.Element => {
  const ds = useMemo(() => {
    return groupKey ? Array.from(new Set(data.map((d) => d[groupKey]))) : []
  }, [data, groupKey])

  const opts = useMemo(
    () => ({
      fill: true,
      datalabels: {
        display: false,
        anchor: 'end',
        align: 'start',
        color: '#FFF',
        font: {
          size: '12',
          weight: 'bold',
          fontFamily: 'Spartan',
        },
        padding: {
          top: 32,
        },
      },
    }),
    [],
  )

  const labels = useMemo(() => {
    if (xKey) return Array.from(new Set(data?.map((d) => d[xKey])))
  }, [data, xKey])

  const data_for_key = useCallback(
    (key: string | number) => {
      if (xKey && groupKey) {
        const entries = labels?.map((l) => {
          const entry = data.find((x) => x[xKey] === l && x[groupKey] === key) ?? {
            [yKey]: 0,
          }
          return entry[yKey]
        })
        return entries
      }
    },
    [data, labels, groupKey, yKey, xKey],
  )

  const chartData = {
    labels,
    datasets: ds.map((d, i) => ({
      label: data.find((x) => x[groupKey] === d)?.[groupKey],
      data: data_for_key(d),
      backgroundColor: colors[i],
      ...opts,
    })),
  }
  if (isLoading) {
    return (
      <div className={'w-full h-full flex items-center justify-center'}>Loading...</div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={'w-full h-full flex items-center justify-center opacity-50'}>
        <Bar
          options={{
            ...options,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                mode: 'index' as const,
                intersect: false,
              },
            },
          }}
          data={{
            labels: data.map((v) => v[xKey]),
            datasets: [
              {
                label: '',
                data: [],
                borderWidth: 0,
                datalabels: {
                  display: false,
                },
              },
            ],
          }}
        />
      </div>
    )
  }

  return (
    <div className={'w-full h-full relative'}>
      {isPreviousData && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white opacity-50'} />
      )}
      {/* @ts-ignore */}
      <Bar options={{ ...options, ...customOptions }} data={chartData} />
    </div>
  )
}

export default StackedBarChartV2
